/**
 * zero-price-contact.js
 *
 * What this file does:
 * - Opens/closes the zero-price contact modal
 * - Cleans up Swym wishlist injections inside the modal (without breaking the submit button)
 * - Handles Shopify CAPTCHA behavior gracefully:
 *   - If Shopify triggers a CAPTCHA, it will take over the page (Shopify behavior)
 *   - We warn the user before the redirect
 *   - We restore the modal + draft values after they return
 *
 * IMPORTANT:
 * - This file intentionally lets the contact form submit normally (no AJAX)
 *   so CAPTCHA can work reliably in the Theme Customizer.
 */

(function () {
  const DRAFT_KEY = "zeroPriceContactDraft";
  const PENDING_KEY = "zeroPriceContactPending";
  const RETURN_URL_KEY = "zeroPriceContactReturnUrl";

  function fixSwymInModal(modal) {
    if (!modal) return;

    // Remove ONLY the wishlist UI container (does not contain the submit button)
    modal.querySelectorAll(".swym-atw-button-container").forEach((el) => el.remove());

    // Ensure our submit stays visible and in the right place
    const submit = modal.querySelector("[data-zero-price-submit]");
    const actions = modal.querySelector(".zero-price-modal__actions");
    if (submit && actions && !actions.contains(submit)) {
      actions.appendChild(submit);
    }

    // IMPORTANT: do NOT remove .swym-atw-btn-wrapper, since it may contain our submit button
  }

  function setModalOpen(modal) {
    if (!modal) return;
    modal.removeAttribute("hidden");
    modal.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("zero-price-modal-open");
  }

  function setModalClosed(modal) {
    if (!modal) return;
    modal.setAttribute("hidden", "hidden");
    modal.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("zero-price-modal-open");
  }

  function ensureCaptchaNote(modal) {
    if (!modal) return;

    // Put note near the subtitle if present; fallback near actions.
    let note = modal.querySelector("[data-zero-price-captcha-note]");
    if (note) return;

    note = document.createElement("p");
    note.setAttribute("data-zero-price-captcha-note", "");
    note.className = "zero-price-modal__captcha-note";
    note.textContent =
      "If you get a CAPTCHA, your inquiry will be sent once it’s completed successfully.";

    const subtitle = modal.querySelector(".zero-price-modal__subtitle");
    const actions = modal.querySelector(".zero-price-modal__actions");

    if (subtitle && subtitle.parentNode) {
      subtitle.parentNode.insertBefore(note, subtitle.nextSibling);
    } else if (actions && actions.parentNode) {
      actions.parentNode.insertBefore(note, actions);
    } else {
      modal.appendChild(note);
    }
  }

  function clearInlineErrors(modal) {
    if (!modal) return;
    modal.querySelectorAll("[data-zero-price-error]").forEach((el) => el.remove());
  }

  function showInlineError(modal, text) {
    if (!modal) return;

    let node = modal.querySelector("[data-zero-price-error]");
    if (!node) {
      node = document.createElement("p");
      node.setAttribute("data-zero-price-error", "");
      node.className = "zero-price-modal__error";
      const actions = modal.querySelector(".zero-price-modal__actions");
      (actions ? actions.after(node) : modal.appendChild(node));
    }
    node.textContent = text;
  }

  function saveDraft(form) {
    try {
      const fd = new FormData(form);
      const saved = {};
      for (const [k, v] of fd.entries()) {
        if (typeof v === "string") saved[k] = v;
      }
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(saved));
      sessionStorage.setItem(PENDING_KEY, "1");
      sessionStorage.setItem(RETURN_URL_KEY, window.location.href);
    } catch (_) {}
  }

  function restoreDraftIntoModal(modal) {
    if (!modal) return;
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return;

      const draft = JSON.parse(raw);
      if (!draft || typeof draft !== "object") return;

      Object.keys(draft).forEach((name) => {
        // CSS.escape exists in modern browsers; fallback if needed
        const selectorName =
          window.CSS && CSS.escape ? CSS.escape(name) : name.replace(/"/g, '\\"');
        const el = modal.querySelector(`[name="${selectorName}"]`);
        if (el && "value" in el) el.value = draft[name];
      });
    } catch (_) {}
  }

  function openModal(modal) {
    if (!modal) return;

    setModalOpen(modal);
    clearInlineErrors(modal);

    // Ensure the user guidance text is present
    ensureCaptchaNote(modal);

    // If you have success wrappers, reset them on open
    const formWrap = modal.querySelector("[data-zero-price-form]");
    const successWrap = modal.querySelector("[data-zero-price-success]");
    if (formWrap && successWrap) {
      formWrap.hidden = false;
      successWrap.hidden = true;
    }

    // Swym sometimes injects late — clean a few times
    fixSwymInModal(modal);
    let tries = 0;
    const iv = setInterval(() => {
      fixSwymInModal(modal);
      tries += 1;
      if (tries >= 10) clearInterval(iv); // ~1s
    }, 100);
  }

  function closeModal(modal) {
    setModalClosed(modal);
  }

  // Restore-after-CAPTCHA / refresh behavior:
  // If user submitted and Shopify required a CAPTCHA, they will be returned to the product page.
  // We reopen the modal and show a confirmation banner.
  (function restoreAfterCaptcha() {
    try {
      const pending = sessionStorage.getItem(PENDING_KEY);
      if (pending !== "1") return;

      // We are back after a submit/captcha flow
      sessionStorage.removeItem(PENDING_KEY);

      const modal = document.querySelector(".zero-price-modal");
      if (!modal) return;

      openModal(modal);

      // Restore draft values so user can see/confirm what they sent
      restoreDraftIntoModal(modal);

      // If you have success wrappers, show the success panel (best UX)
      const formWrap = modal.querySelector("[data-zero-price-form]");
      const successWrap = modal.querySelector("[data-zero-price-success]");
      if (formWrap && successWrap) {
        formWrap.hidden = true;
        successWrap.hidden = false;
      } else {
        // Otherwise show a clear message
        showInlineError(
          modal,
          "If you completed the CAPTCHA and clicked Send inquiry, your message was sent successfully. If you’re not sure, you can submit again."
        );
      }

      // Re-enable submit if it was disabled pre-navigation
      const submitBtn = modal.querySelector("[data-zero-price-submit]");
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.removeAttribute("aria-busy");
        // Optional: rename button text if theme changed it
        const txt = submitBtn.querySelector("[data-add-to-cart-text]") || submitBtn;
        if (txt && txt.textContent) txt.textContent = "Send inquiry";
      }
    } catch (_) {}
  })();

  // Click handling (open/close)
  document.addEventListener("click", function (e) {
    const openBtn = e.target.closest("[data-zero-price-contact-open]");
    if (openBtn) {
      e.preventDefault();
      const id = openBtn.getAttribute("aria-controls");
      const modal = id ? document.getElementById(id) : null;
      openModal(modal);
      return;
    }

    const closeBtn = e.target.closest("[data-zero-price-contact-close]");
    if (closeBtn) {
      e.preventDefault();
      const modal = closeBtn.closest(".zero-price-modal");
      closeModal(modal);
    }
  });

  // Escape closes modal
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;
    document.querySelectorAll(".zero-price-modal:not([hidden])").forEach(closeModal);
  });

  // Theme editor safety
  document.addEventListener("shopify:section:unload", function () {
    document.querySelectorAll(".zero-price-modal:not([hidden])").forEach(closeModal);
  });

  // Submit behavior:
  // We DO NOT prevent Shopify from submitting, so CAPTCHA works.
  // We save draft + mark pending so after navigation we can reopen modal.
  document.addEventListener("submit", function (e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    const modal = form.closest(".zero-price-modal");
    if (!modal) return;

    const submitBtn = form.querySelector("[data-zero-price-submit]");
    if (!submitBtn) return;

    clearInlineErrors(modal);
    ensureCaptchaNote(modal);
    fixSwymInModal(modal);

    // Native validation — if invalid, stop here and keep modal open
    if (!form.checkValidity()) {
      e.preventDefault();
      form.reportValidity();
      return;
    }

    // Save draft + mark pending so we can show confirmation after Shopify returns
    saveDraft(form);

    // Disable button to reduce double clicks (Shopify will navigate if CAPTCHA triggers)
    submitBtn.disabled = true;
    submitBtn.setAttribute("aria-busy", "true");

    // Rename the button text (requested)
    submitBtn.textContent = "Sending…";
  });
})();
